import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useBMP } from '../context/BMPContext';
import { useTranslation } from 'react-i18next';
import './AnalyticsView.css';

const API = process.env.REACT_APP_API_URL;

function fmt(val) {
  return parseFloat(val || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function AnalyticsView() {
  const { t } = useTranslation();
  const { isProducer } = useBMP();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/market/analytics/`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="bmp-loading">{t('loading_dots')}</div>;
  if (!data) return <div className="bmp-empty">{t('bmp_analytics_error')}</div>;

  return (
    <div className="analytics">
      <div className="bmp-section-header">
        <h2>{t('bmp_analytics_title')}</h2>
      </div>

      {/* KPI cards */}
      <div className="analytics-kpis">
        <div className="analytics-kpi">
          <span className="analytics-kpi-val">{data.total_transactions}</span>
          <span className="analytics-kpi-label">{t('bmp_analytics_transactions')}</span>
        </div>
        <div className="analytics-kpi">
          <span className="analytics-kpi-val">{data.total_boxes_sold.toLocaleString('es-EC')}</span>
          <span className="analytics-kpi-label">{isProducer ? t('bmp_analytics_boxes_sold') : t('bmp_analytics_boxes_bought')}</span>
        </div>
        <div className="analytics-kpi">
          <span className="analytics-kpi-val analytics-kpi-val--green">${fmt(data.total_revenue)}</span>
          <span className="analytics-kpi-label">{t('bmp_analytics_revenue')}</span>
        </div>
        <div className="analytics-kpi">
          <span className="analytics-kpi-val">${fmt(data.avg_price_per_box)}</span>
          <span className="analytics-kpi-label">{t('bmp_analytics_avg_price')}</span>
        </div>
        {data.market_price_ref && (
          <div className="analytics-kpi">
            <span className="analytics-kpi-val">${fmt(data.market_price_ref)}</span>
            <span className="analytics-kpi-label">{t('bmp_analytics_market_price')}</span>
            {data.price_vs_market !== null && (
              <span className={`analytics-kpi-diff${data.price_vs_market >= 0 ? ' analytics-kpi-diff--up' : ' analytics-kpi-diff--down'}`}>
                {data.price_vs_market >= 0 ? '+' : ''}${fmt(data.price_vs_market)}
              </span>
            )}
          </div>
        )}
        <div className="analytics-kpi">
          <span className="analytics-kpi-val">{data.pending_bids}</span>
          <span className="analytics-kpi-label">{t('bmp_analytics_pending')}</span>
        </div>
      </div>

      {/* Views & Rating */}
      <div className="analytics-row">
        {isProducer && (
          <div className="analytics-card bmp-card">
            <h3>{t('bmp_analytics_visibility')}</h3>
            <div className="analytics-big-num">{data.total_views.toLocaleString('es-EC')}</div>
            <span className="analytics-big-label">{t('bmp_analytics_total_views')}</span>
          </div>
        )}
        <div className="analytics-card bmp-card">
          <h3>{t('bmp_analytics_reputation')}</h3>
          {data.rating_avg ? (
            <>
              <div className="analytics-big-num analytics-stars">
                {'★'.repeat(Math.round(data.rating_avg))}{'☆'.repeat(5 - Math.round(data.rating_avg))}
              </div>
              <span className="analytics-big-label">{data.rating_avg}/5 ({data.rating_count} {t('bmp_analytics_ratings')})</span>
            </>
          ) : (
            <span className="analytics-big-label">{t('bmp_analytics_no_ratings')}</span>
          )}
        </div>
      </div>

      {/* Top farms */}
      {data.top_farms && data.top_farms.length > 0 && (
        <div className="analytics-section">
          <h3>{t('bmp_analytics_top_farms')}</h3>
          <div className="analytics-table">
            <table>
              <thead>
                <tr>
                  <th>{t('bmp_farm_name')}</th>
                  <th>{t('bmp_analytics_bids_total')}</th>
                  <th>{t('bmp_analytics_tx_total')}</th>
                  <th>{t('bmp_analytics_views')}</th>
                </tr>
              </thead>
              <tbody>
                {data.top_farms.map((f, i) => (
                  <tr key={i}>
                    <td><strong>{f.name}</strong></td>
                    <td>{f.bids_total}</td>
                    <td>{f.transactions_total}</td>
                    <td>{f.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
